import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Send, Loader2, CheckCircle2, Clock, Calendar } from "lucide-react";
import { api } from "@/lib/api";
import { BTN, BRAND } from "@/lib/design";

const fade = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const INITIAL = { name: "", email: "", phone: "", subject: "General inquiry", message: "" };

export default function Contact() {
    const [form, setForm] = useState(INITIAL);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please fill in your name, email and message.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/contact", form);
            setSubmitted(true);
            setForm(INITIAL);
            toast.success("Message sent — we'll reply soon.");
        } catch (e2) {
            toast.error("Couldn't send. Please call " + BRAND.phone + ".");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page">
            {/* HERO */}
            <section className="bg-[#F7F5F0] pt-16 md:pt-24 pb-12">
                <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-14">
                    <p className="text-[12px] uppercase tracking-[0.2em] text-[#C8A24A] font-medium">
                        Contact Us
                    </p>
                    <h1 className="mt-3 font-[Outfit] text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
                        Let's talk.
                    </h1>
                    <p className="mt-6 text-[17px] leading-relaxed text-[#6F6B63] max-w-3xl">
                        Whether you're exploring placement, looking for
                        information, or want to schedule a confidential
                        consultation — our team is glad to hear from you.
                    </p>
                </div>
            </section>

            {/* CONTACT GRID */}
            <section className="py-16 md:py-20 bg-[#F7F5F0]">
                <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    {/* DETAILS */}
                    <motion.aside {...fade} className="lg:col-span-5 space-y-5">
                        <div className="rounded-3xl bg-[#0E2A47] text-white p-8 md:p-10">
                            <h2 className="font-[Outfit] text-2xl md:text-3xl font-semibold tracking-tight">
                                {BRAND.name}
                            </h2>
                            <p className="mt-2 text-[14px] text-white/70">
                                Trauma-informed residential treatment for male youth in Texas foster care.
                            </p>

                            <ul className="mt-8 space-y-5 text-[15px]">
                                <li className="flex items-start gap-4">
                                    <div className="h-10 w-10 rounded-full bg-white/10 grid place-items-center">
                                        <MapPin className="h-4.5 w-4.5 text-[#C8A24A]" />
                                    </div>
                                    <div>
                                        <p className="text-white/65 text-[12px] uppercase tracking-wider">Service Area</p>
                                        <p className="font-medium mt-0.5" data-testid="contact-service-area">
                                            Serving the Tomball Area
                                        </p>
                                    </div>
                                </li>
                                <li className="flex items-start gap-4">
                                    <div className="h-10 w-10 rounded-full bg-white/10 grid place-items-center">
                                        <Phone className="h-4.5 w-4.5 text-[#C8A24A]" />
                                    </div>
                                    <div>
                                        <p className="text-white/65 text-[12px] uppercase tracking-wider">Phone</p>
                                        <a
                                            href={BRAND.phoneHref}
                                            className="font-bold text-[15.5px] text-[#C8A24A] mt-0.5 block hover:text-white transition-colors"
                                            data-testid="contact-phone-link"
                                        >
                                            {BRAND.phone}
                                        </a>
                                    </div>
                                </li>
                                <li className="flex items-start gap-4">
                                    <div className="h-10 w-10 rounded-full bg-white/10 grid place-items-center">
                                        <Mail className="h-4.5 w-4.5 text-[#C8A24A]" />
                                    </div>
                                    <div>
                                        <p className="text-white/65 text-[12px] uppercase tracking-wider">Email</p>
                                        <a
                                            href={`mailto:${BRAND.email}`}
                                            className="font-medium mt-0.5 block hover:text-[#C8A24A] break-all"
                                            data-testid="contact-email-link"
                                        >
                                            {BRAND.email}
                                        </a>
                                    </div>
                                </li>
                                <li className="flex items-start gap-4">
                                    <div className="h-10 w-10 rounded-full bg-white/10 grid place-items-center">
                                        <Clock className="h-4.5 w-4.5 text-[#C8A24A]" />
                                    </div>
                                    <div>
                                        <p className="text-white/65 text-[12px] uppercase tracking-wider">Response</p>
                                        <p className="font-medium mt-0.5">Within 1 business day</p>
                                    </div>
                                </li>
                            </ul>
                        </div>

                        {/* Service area marker */}
                        <div
                            className="relative rounded-3xl overflow-hidden aspect-[4/3] border border-[#E5DFD3] bg-gradient-to-br from-[#EFEBE0] via-[#F7F5F0] to-[#E8EFE8] grid place-items-center"
                            data-testid="service-area-card"
                        >
                            <div className="text-center px-6">
                                <div className="h-16 w-16 mx-auto rounded-full bg-[#0E2A47] grid place-items-center text-white shadow-lg shadow-[#0E2A47]/20">
                                    <MapPin className="h-7 w-7 text-[#C8A24A]" />
                                </div>
                                <p className="mt-5 font-[Outfit] text-[20px] font-semibold tracking-tight text-[#1A2230]">
                                    Serving the Tomball Area
                                </p>
                                <p className="mt-2 text-[13.5px] text-[#6F6B63] max-w-xs mx-auto leading-relaxed">
                                    Greater Houston · Texas. Exact location shared
                                    with confirmed admissions and care partners.
                                </p>
                            </div>
                        </div>
                    </motion.aside>

                    {/* FORM */}
                    <motion.div {...fade} className="lg:col-span-7" id="schedule">
                        <div className="rounded-3xl bg-white border border-[#E5DFD3] p-8 md:p-10">
                            {submitted ? (
                                <div className="text-center py-10" data-testid="contact-success">
                                    <CheckCircle2 className="h-10 w-10 text-[#0E2A47] mx-auto mb-4" />
                                    <h3 className="font-[Outfit] text-2xl font-semibold tracking-tight">
                                        Message sent
                                    </h3>
                                    <p className="mt-3 text-[15px] text-[#6F6B63] max-w-md mx-auto">
                                        Thanks for reaching out — we'll be in touch within one business day.
                                    </p>
                                    <button
                                        onClick={() => setSubmitted(false)}
                                        className={`mt-7 ${BTN.outline}`}
                                        data-testid="contact-send-another"
                                    >
                                        Send another message
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                                    <div>
                                        <h3 className="font-[Outfit] text-2xl font-semibold tracking-tight">
                                            Send us a message
                                        </h3>
                                        <p className="mt-2 text-[14px] text-[#6F6B63]">
                                            We read every message. For urgent matters please call.
                                        </p>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <div>
                                            <label className="rtc-label">Full name *</label>
                                            <input
                                                required
                                                className="rtc-input"
                                                value={form.name}
                                                onChange={update("name")}
                                                data-testid="contact-name"
                                            />
                                        </div>
                                        <div>
                                            <label className="rtc-label">Email *</label>
                                            <input
                                                required
                                                type="email"
                                                className="rtc-input"
                                                value={form.email}
                                                onChange={update("email")}
                                                data-testid="contact-email"
                                            />
                                        </div>
                                        <div>
                                            <label className="rtc-label">Phone</label>
                                            <input
                                                className="rtc-input"
                                                value={form.phone}
                                                onChange={update("phone")}
                                                data-testid="contact-phone"
                                            />
                                        </div>
                                        <div>
                                            <label className="rtc-label">Subject</label>
                                            <select
                                                className="rtc-input"
                                                value={form.subject}
                                                onChange={update("subject")}
                                                data-testid="contact-subject"
                                            >
                                                <option>General inquiry</option>
                                                <option>Schedule a Confidential Consultation</option>
                                                <option>Request information</option>
                                                <option>Partnership / agency</option>
                                                <option>Other</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="rtc-label">Message *</label>
                                        <textarea
                                            required
                                            rows={6}
                                            className="rtc-input"
                                            value={form.message}
                                            onChange={update("message")}
                                            data-testid="contact-message"
                                        />
                                    </div>
                                    <div className="flex flex-wrap gap-3">
                                        <button
                                            type="submit"
                                            disabled={submitting}
                                            className={`${BTN.primary} w-full sm:w-auto`}
                                            data-testid="contact-submit"
                                        >
                                            {submitting ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <Send className="h-4 w-4" />
                                            )}
                                            Send message
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setForm((f) => ({ ...f, subject: "Schedule a Confidential Consultation" }))}
                                            className={BTN.secondary}
                                            data-testid="schedule-consult"
                                        >
                                            <Calendar className="h-4 w-4" />
                                            Schedule a Confidential Consultation
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
}
